import { configureApi, loginApiV1AuthLoginPost } from '@closet-theory/api';
import { createSessionFetch, type RefreshedSession } from '@ui/console/session-fetch';
import {
  invalidatesSession,
  isAdminRole,
  resolveApiBaseUrl,
  tokenIsExpired,
  type AdminRole,
} from './auth-policy';

const TOKEN_KEY = 'closet-theory-admin-access-token';
const REFRESH_KEY = 'closet-theory-admin-refresh-token';
const ROLE_KEY = 'closet-theory-admin-role';
const PASSWORD_CHANGE_KEY = 'closet-theory-admin-password-change-required';
export const SESSION_EXPIRED_EVENT = 'closet-theory-admin-session-expired';
const configuredApiBaseUrl: unknown = import.meta.env.VITE_API_BASE_URL;
if (import.meta.env.PROD && typeof configuredApiBaseUrl !== 'string') {
  throw new Error('VITE_API_BASE_URL is required for production Admin builds.');
}
const API_BASE_URL = resolveApiBaseUrl(
  typeof configuredApiBaseUrl === 'string' ? configuredApiBaseUrl : 'http://localhost:8000',
  import.meta.env.PROD,
);

/** The resolved API origin, for the few calls not yet in the generated client. */
export function apiBaseUrl(): string {
  return API_BASE_URL;
}

function storeSession(session: RefreshedSession): void {
  sessionStorage.setItem(TOKEN_KEY, session.access_token);
  sessionStorage.setItem(REFRESH_KEY, session.refresh_token);
  sessionStorage.setItem(ROLE_KEY, session.role);
  sessionStorage.setItem(PASSWORD_CHANGE_KEY, session.password_change_required ? 'true' : 'false');
}

const session = createSessionFetch({
  apiBaseUrl: API_BASE_URL,
  readTokens: () => ({
    accessToken: sessionStorage.getItem(TOKEN_KEY) ?? undefined,
    refreshToken: sessionStorage.getItem(REFRESH_KEY) ?? undefined,
  }),
  writeSession: (refreshed) => {
    // A role changed server-side to one Admin does not admit ends the session
    // rather than carrying on under the old one.
    if (!isAdminRole(refreshed.role)) return false;
    storeSession(refreshed);
    return true;
  },
  onSessionExpired: () => expireSession(),
  isExpired: (token) => tokenIsExpired(token),
});

/**
 * fetch for the API origin that carries the session's bearer and refreshes it
 * when it has expired. Use this, not the global fetch, for any API call made
 * outside the generated client.
 */
export const adminFetch = session.fetch;

/**
 * The stored access token while the session is still live - valid, or expired
 * but refreshable (adminFetch refreshes it before sending). Undefined, and
 * signed out, once neither holds.
 */
export function accessToken(): string | undefined {
  const token = sessionStorage.getItem(TOKEN_KEY) ?? undefined;
  const refresh = sessionStorage.getItem(REFRESH_KEY);
  const role = sessionStorage.getItem(ROLE_KEY);
  if (!token || !role || !isAdminRole(role) || (tokenIsExpired(token) && !refresh)) {
    signOut();
    return undefined;
  }
  return token;
}

export function configureAdminApi(): void {
  configureApi(API_BASE_URL, accessToken(), { fetch: adminFetch });
}

export async function signIn(email: string, password: string): Promise<boolean> {
  configureApi(API_BASE_URL, undefined, { fetch: adminFetch });
  const result = await loginApiV1AuthLoginPost({ body: { email, password } });
  if (result.error || !result.data?.access_token)
    throw new Error('Email or password is incorrect.');
  if (!isAdminRole(result.data.role)) {
    signOut();
    throw new Error('This account is not authorized for Admin.');
  }
  storeSession(result.data);
  configureAdminApi();
  return Boolean(result.data.password_change_required);
}

export function signOut(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(ROLE_KEY);
  sessionStorage.removeItem(PASSWORD_CHANGE_KEY);
  configureApi(API_BASE_URL, undefined, { fetch: adminFetch });
}

/**
 * Ends the session and sends the operator to sign in. A failed refresh and
 * the 401 it leaves behind can both land here; the second navigation is a
 * no-op, and dispatching on every 401 keeps any one of them sufficient.
 */
function expireSession(): void {
  signOut();
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

export function passwordChangeRequired(): boolean {
  return sessionStorage.getItem(PASSWORD_CHANGE_KEY) === 'true';
}

export function currentRole(): AdminRole | undefined {
  const role = sessionStorage.getItem(ROLE_KEY);
  return role && isAdminRole(role) ? role : undefined;
}

/** A 401 that survived adminFetch's refresh and retry: the session is over. */
export function handleUnauthorized(status: number | undefined): void {
  if (!invalidatesSession(status)) return;
  expireSession();
}
